import { useRef, useState, useEffect, useCallback } from 'react';
import { Box } from '@mantine/core';
import GameHeader from '../components/GameHeader';
import DrawingCanvas from '../components/DrawingCanvas';
import SketchButton from '../components/SketchButton';

function PlayerDrawingScreen({ gameName, roomCode, onReconnect, onQuit, onSubmitDrawing, buttonLabel = 'SUBMIT', disabled, instruction, responseTimeLeft, disableTimers, isJudge, ...canvasProps }) {
  const canvasRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!disabled && isSubmitting) setIsSubmitting(false);
  }, [disabled, isSubmitting]);

  useEffect(() => {
    if (isSubmitting) {
      const timeout = setTimeout(() => setIsSubmitting(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isSubmitting]);

  const handleSubmit = useCallback(() => {
    const drawing = canvasRef.current?.getDrawing();
    if (!drawing) return;
    setIsSubmitting(true);
    onSubmitDrawing(drawing);
  }, [onSubmitDrawing]);

  // Auto-submit partial drawing when timer expires
  const hasAutoSubmitted = useRef(false);
  useEffect(() => {
    if (!disableTimers && responseTimeLeft === 0 && !disabled && !isSubmitting && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      handleSubmit();
    }
  }, [responseTimeLeft, disableTimers, disabled, isSubmitting, handleSubmit]);

  const timerSeconds = !disableTimers ? responseTimeLeft : 0;

  return (
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        touchAction: 'none',
      }}
    >
      <GameHeader
        gameName={gameName}
        roomCode={roomCode}
        onReconnect={onReconnect}
        onQuit={onQuit}
        timerSeconds={timerSeconds}
      />
      {instruction && (
        <Box
          style={{
            flexShrink: 0,
            padding: '0.625rem 1rem',
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1.3rem',
            backgroundColor: instruction.color === 'yellow' ? 'rgba(255, 215, 0, 0.2)' : 'rgba(128, 0, 128, 0.1)',
            color: instruction.color === 'yellow' ? '#b8860b' : '#7b2d8e',
          }}
        >
          {instruction.text}
        </Box>
      )}
      <DrawingCanvas ref={canvasRef} disabled={disabled} {...canvasProps} />
      <Box ta="center" p="sm" style={{ flexShrink: 0 }}>
        <SketchButton onClick={handleSubmit} disabled={disabled || isSubmitting}>
          {buttonLabel}
        </SketchButton>
      </Box>
    </Box>
  );
}

export default PlayerDrawingScreen;
